import axios, { AxiosInstance, AxiosResponse } from "axios";
// helpers
import { pagesUrl, hotelPanelRootUrl, getFutureDateFromCurrentDate } from "./panel-helpers/helpers";
import { fetchAppConfig } from "./appConfig";

const appHeaders = {
  "Content-Type": "application/json"
};

type RefreshQueueItem = {
  resolve: (value: any) => void;
  reject: (error: any) => void;
  config: any;
};

export let axiosInstance: any;

let isRefreshing = false;

let refreshQueue: RefreshQueueItem[] = [];

const shouldRefreshAccessToken = (error: any) => {
  const status = error?.response?.status ?? error?.response?.data?.code;
  const message = error?.response?.data?.message?.toLowerCase?.() || "";

  return (
    status === 401 ||
    ((status === 403 || error?.response?.data?.code === 401) &&
      (message.includes("expired jwt token") || message.includes("token expired")))
  );
};

const processRefreshQueue = (instance: AxiosInstance, error?: any) => {
  refreshQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
      return;
    }

    resolve(instance.request(config));
  });
  refreshQueue = [];
};

const createQueryString = (params: any) => {
  const paramsKeys = Object.keys(params);
  let queryString = "";
  paramsKeys.forEach((key, index) => {
    if (Array.isArray(params[key])) {
      if (params[key].length > 0) {
        queryString += index === 0 ? "?" : "&";
        params[key].forEach((el: any, i: any) => {
          if (i > 0) {
            queryString += "&";
          }
          queryString += `${encodeURIComponent(`${key}[${i}]`)}=${encodeURIComponent(el)}`;
        });
      }
    } else {
      queryString += index === 0 ? "?" : "&";
      queryString += `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`;
    }
  });
  return queryString;
};

class ApiHelper {
  static axiosInstance: AxiosInstance | null = null;
  static requestQueue: (() => void)[] = [];

  static async setAxiosInstance(instance: AxiosInstance) {
    this.axiosInstance = instance;
    // Release queued requests once Axios instance is ready
    this.executeQueuedRequests();
  }

  static async executeQueuedRequests() {
    // Iterate over the requestQueue and execute each queued request
    this.requestQueue.forEach(request => request());
    // Clear the queue after executing all requests
    this.requestQueue = [];
  }

  static async get<T>(
    resourceUrl: string,
    params: any = {},
    additionalHeaders?: object,
    responseType?: any,
    customHeaders?: any
  ): Promise<AxiosResponse<T>> {
    const url = `${resourceUrl + createQueryString(params)}`;
    const requestConfig = {
      method: "get",
      url,
      withCredentials: false,
      headers: customHeaders
        ? {
            ...customHeaders
          }
        : {
            ...appHeaders,
            ...additionalHeaders
          },
      responseType: responseType
    };

    if (!this.axiosInstance) {
      // Queue the request if Axios instance is not ready
      return new Promise((resolve, reject) => {
        this.requestQueue.push(() => {
          resolve(this.axiosInstance!.request<T>(requestConfig));
        });
      });
    }

    // If Axios instance is ready, send the request immediately
    return this.axiosInstance.request<T>(requestConfig);
  }

  static async post<T>(
    resourceUrl: string,
    params: any = {},
    bodyData: any,
    customHeaders?: any
  ): Promise<AxiosResponse<T>> {
    const url = `${resourceUrl + createQueryString(params)}`;

    const requestConfig = {
      method: "post",
      url,
      data: bodyData,
      headers: customHeaders
        ? { ...customHeaders }
        : {
            ...appHeaders
          }
    };

    if (!this.axiosInstance) {
      // Queue the request if Axios instance is not ready
      return new Promise((resolve, reject) => {
        this.requestQueue.push(() => {
          resolve(this.axiosInstance!.request<T>(requestConfig));
        });
      });
    }

    // If Axios instance is ready, send the request immediately
    return this.axiosInstance.request<T>(requestConfig);
  }

  static async put<T>(resourceUrl: string, bodyData: any): Promise<AxiosResponse<T>> {
    const url = `${resourceUrl}`;
    const requestConfig = {
      method: "put",
      url,
      data: bodyData,
      headers: appHeaders
    };

    if (!this.axiosInstance) {
      // Queue the request if Axios instance is not ready
      return new Promise((resolve, reject) => {
        this.requestQueue.push(() => {
          resolve(this.axiosInstance!.request<T>(requestConfig));
        });
      });
    }

    // If Axios instance is ready, send the request immediately
    return this.axiosInstance.request<T>(requestConfig);
  }

  static async patch<T>(
    resourceUrl: string,
    params: any = {},
    bodyData: any,
    customHeaders?: any
  ): Promise<AxiosResponse<T>> {
    const url = `${resourceUrl + createQueryString(params)}`;
    const requestConfig = {
      method: "patch",
      url,
      data: bodyData,
      headers: customHeaders
        ? { ...customHeaders }
        : {
            ...appHeaders
          }
    };

    if (!this.axiosInstance) {
      // Queue the request if Axios instance is not ready
      return new Promise((resolve, reject) => {
        this.requestQueue.push(() => {
          resolve(this.axiosInstance!.request<T>(requestConfig));
        });
      });
    }

    // If Axios instance is ready, send the request immediately
    return this.axiosInstance.request<T>(requestConfig);
  }

  static async delete<T>(resourceUrl: string): Promise<AxiosResponse<T>> {
    const url = `${resourceUrl}`;
    const requestConfig = { method: "delete", url, headers: appHeaders };

    if (!this.axiosInstance) {
      // Queue the request if Axios instance is not ready
      return new Promise((resolve, reject) => {
        this.requestQueue.push(() => {
          resolve(this.axiosInstance!.request<T>(requestConfig));
        });
      });
    }

    // If Axios instance is ready, send the request immediately
    return this.axiosInstance.request<T>(requestConfig);
  }
}

async function refreshAccessToken(): Promise<boolean> {
  try {
    const refreshToken = JSON.parse(localStorage.getItem(`${hotelPanelRootUrl}-token`) || "{}")?.refresh;
    // @ts-ignore
    const cookieUrl = JSON.parse(localStorage.getItem(`${hotelPanelRootUrl}-url`));
    // @ts-ignore
    const username = JSON.parse(localStorage.getItem(`${hotelPanelRootUrl}-username`));
    // @ts-ignore
    const loggedIn = JSON.parse(localStorage.getItem("loggedIn"));

    // Make a request to refresh the access token
    const formData = new FormData();
    formData.append("client_id", username);
    formData.append("grant_type", "refresh_token");
    formData.append("refresh_token", refreshToken);

    const res = await fetch(`${cookieUrl}/api/v1/refresh-token`, {
      method: "POST",
      body: formData
    });

    const responseToken = await res.json();

    if (!res.ok || !responseToken?.access_token) {
      throw new Error(responseToken?.message || "Failed to refresh access token");
    }

    // Store tokenData to localStorage
    const tokenData = {
      token: responseToken.access_token,
      refresh: responseToken.refresh_token
    };
    localStorage.setItem(`${hotelPanelRootUrl}-token`, JSON.stringify(tokenData));

    // check keep me logged in checkbox
    if (loggedIn) {
      // Store expires in a cookie with expires date - keep cookie in browser
      document.cookie = `${hotelPanelRootUrl}-expires=${getFutureDateFromCurrentDate(
        30
      )}; expires="${getFutureDateFromCurrentDate(30)}"; path=/; Secure`;
    } else {
      // Store expires in a cookie without expires date
      document.cookie = `${hotelPanelRootUrl}-expires=${getFutureDateFromCurrentDate(30)}; path=/; Secure`;
    }

    // Backward compatibility: Store cookies with expires date set to -1 to avoid including them to request header
    // TODO: Remove after a period of time
    document.cookie = `${hotelPanelRootUrl}-cookie= ; path=/; expires=-1`;
    document.cookie = `${hotelPanelRootUrl}-cookie-refresh= ; expires=-1; path=/; Secure`;

    // Return true to indicate successful token refresh
    return true;
  } catch (error: any) {
    // Handle error while refreshing token
    console.error("Error refreshing access token:", error);

    // Backward compatibility: Store cookies with expires date set to -1 to avoid including them to request header
    // TODO: Remove after a period of time
    document.cookie = `${hotelPanelRootUrl}-cookie= ; path=/; expires=-1`;
    document.cookie = `${hotelPanelRootUrl}-cookie-refresh= ; path=/; expires=-1`;
    document.cookie = `${hotelPanelRootUrl}-username= ; path=/; expires=-1`;

    // delete cookie by setting expires date in the past
    document.cookie = `${hotelPanelRootUrl}-expires= ; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;

    const tokenData = {
      token: "",
      refresh: ""
    };

    localStorage.setItem(`${hotelPanelRootUrl}-token`, JSON.stringify(tokenData));
    localStorage.setItem(`${hotelPanelRootUrl}-username`, JSON.stringify("/"));

    window.location.pathname = `/${hotelPanelRootUrl}/${pagesUrl.login}`;

    return false;
  }
}

// Configure Axios and set up interceptors
async function configureAxios() {
  try {
    const config = await fetchAppConfig();

    const axiosInstance = axios.create({
      baseURL: config.api_url,
      withCredentials: false
    });

    localStorage.setItem(`${hotelPanelRootUrl}-url`, JSON.stringify(config.oauth_url));

    document.cookie = `${hotelPanelRootUrl}-cookie-url= ; expires=-1; path=/; Secure`;

    // Add a request interceptor to add the access token to each request
    axiosInstance.interceptors.request.use(
      config => {
        // Retrieve the access token from cookie
        const accessToken = JSON.parse(localStorage.getItem(`${hotelPanelRootUrl}-token`) || "{}")?.token;

        // Add the access token to the request headers
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }

        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );

    // Add a response interceptor to handle token expiration
    axiosInstance.interceptors.response.use(
      response => {
        // Return a successful response directly
        return response;
      },
      async error => {
        const originalRequest = error.config || {};

        if (!shouldRefreshAccessToken(error) || originalRequest._retry) {
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            refreshQueue.push({
              resolve,
              reject,
              config: originalRequest
            });
          });
        }

        isRefreshing = true;

        try {
          const refreshed = await refreshAccessToken();

          if (!refreshed) {
            throw error;
          }

          processRefreshQueue(axiosInstance);
          return axiosInstance.request(originalRequest);
        } catch (refreshError) {
          processRefreshQueue(axiosInstance, refreshError);
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
    );

    // Configure the Axios instance using ApiHelper
    await ApiHelper.setAxiosInstance(axiosInstance);
  } catch (error) {
    console.error("Error configuring Axios:", error);
  }
}

// Call the function to configure Axios
configureAxios();

export default ApiHelper;
